import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { PersistedTable } from '~/components/Shared/PersistedTable/PersistedTable';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { exportXlsx } from '~/eds/src/utils/exportXlsx';
import { TableContextType } from '~/enums/TableContextType';
import { Filter } from '~/evifields/src/types/Filter';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useTableSettings } from '~/hooks/useTableSettings';
import { api } from '~/redux/api/createApi';
import { coerceRtkqError } from '~/redux/api/utils';
import { slices } from '~/redux/slices';

import {
  ATTEMPT_MODEL_ACTION_KEYS,
  DEFAULT_LIMIT_ATTEMPTS,
  initialAttemptsTableSettings,
} from '../constants';
import { AttemptsFilters } from '../Filters/AttemptsFilters';
import { Attempt, AttemptType } from '../types';
import { getAttemptsDisabledTooltip, getStatusChip } from '../utils';

export const Attempts = () => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const enableWebhookExtraFeatures = useFlag(FlagType.EnableWebhooksComponents);
  const intl = useIntl();

  const { tableSettings = initialAttemptsTableSettings } = useTableSettings(
    TableContextType.Attempts,
  );

  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(tableSettings.pageSize);
  const [sortBy, setSortBy] = useState<
    { id: string; desc: boolean } | undefined
  >(tableSettings.sortBy);

  const filters = useSelector(slices.webhooks.selectors.selectedFilters);

  const {
    data = { results: [], total: 0 },
    isLoading: isLoadingAttempts,
    error: attemptsError,
  } = api.endpoints.getAttempts.useQuery({
    filters,
    page: pageIndex,
    pageSize,
    sortBy,
  });

  const [getAttemptsExport, { isLoading: isLoadingExportAttempts }] =
    api.endpoints.getAttemptsExport.useLazyQuery();

  const columns = [
    {
      key: 'url',
      title: intl.formatMessage({
        id: 'Attempts.endpointUrlTitle',
        defaultMessage: 'Endpoint URL',
        description: 'Column title for endpoint URL',
      }),
      cellType: 'text',
      mapCellProps: (attempt: Attempt) => ({
        text: attempt.endpointUrl,
      }),
    },
    {
      key: 'eventType',
      title: intl.formatMessage({
        id: 'Attempts.eventTypeTitle',
        defaultMessage: 'Event Type',
        description: 'Column title for event type',
      }),
      cellType: 'text',
      mapCellProps: (attempt: Attempt) => ({
        text: attempt.eventType,
      }),
    },
    {
      key: 'status',
      title: intl.formatMessage({
        id: 'Attempts.statusTitle',
        defaultMessage: 'Status',
        description: 'Column title for status',
      }),
      cellType: 'chips',
      mapCellProps: (attempt: Attempt) => {
        const { text, status } = getStatusChip(attempt.status, intl);
        return {
          chips: [
            {
              text,
              status,
            },
          ],
        };
      },
    },
    {
      key: 'messageId',
      title: intl.formatMessage({
        id: 'Attempts.messageIdTitle',
        defaultMessage: 'Message ID',
        description: 'Column title for message ID',
      }),
      cellType: 'text',
      mapCellProps: (attempt: Attempt) => ({
        text: attempt.messageId,
      }),
    },
    {
      key: 'dateDelivered',
      title: intl.formatMessage({
        id: 'Attempts.dateSentTitle',
        defaultMessage: 'Date Sent',
        description: 'Column title for date sent',
      }),
      cellType: 'datetime',
      mapCellProps: (attempt: Attempt) => ({
        datetime: attempt.dateSent,
        format: 'short_datetime',
      }),
      width: 'm',
    },
  ];

  const handlePaginate = useCallback(
    ({ pageIndex }: { pageIndex: number }) => {
      setPageIndex(pageIndex);
    },
    [pageIndex],
  );

  const handleUpdate = useCallback(
    (state: any, action?: any) => {
      if (action?.type === 'toggleSortBy') {
        const updatedSortBy = state.sortBy[0];

        setSortBy(updatedSortBy);
      }
      if (action?.type === 'setPageSize') {
        setPageSize(action.pageSize);
      }
    },
    [sortBy],
  );

  const handleUpdateAttempt = (attempt: Attempt, action: AttemptType) => {
    dispatch(slices.webhooks.actions.setActiveAttempt(attempt));
    dispatch(slices.webhooks.actions.setActiveAttemptType(action));
  };

  useEffect(() => {
    if (coerceRtkqError(attemptsError)?.response?.data?.errors[0].detail) {
      toast({
        status: 'danger',
        message: `${
          coerceRtkqError(attemptsError).response.data.errors[0].detail
        }.`,
      });
    }
  }, [attemptsError]);

  const placeholderContent = attemptsError
    ? {
        title: intl.formatMessage({
          id: 'Attempts.errorTitle',
          defaultMessage: 'An error occurred',
          description: 'Error title when attempts fail to load',
        }),
        description: intl.formatMessage({
          id: 'Attempts.errorDescription',
          defaultMessage: 'Please try again.',
          description: 'Error description when attempts fail to load',
        }),
        image: 'error-page-error',
      }
    : data && !data.results.length && !isLoadingAttempts
      ? {
          title: intl.formatMessage({
            id: 'Attempts.noAttemptsTitle',
            defaultMessage: 'No attempts found',
            description: 'Title when no attempts are found',
          }),
          description: intl.formatMessage({
            id: 'Attempts.noAttemptsDescription',
            defaultMessage: 'Try changing your filters.',
            description: 'Description when no attempts are found',
          }),
        }
      : undefined;

  const disabledTooltip = getAttemptsDisabledTooltip(
    isLoadingExportAttempts,
    intl,
  );
  const getRowActions = useCallback(
    (activeAttempt: Attempt) => {
      const actions = [];
      if (activeAttempt.status === 'failed') {
        actions.push({
          key: ATTEMPT_MODEL_ACTION_KEYS.RESEND,
          label: intl.formatMessage({
            id: 'Attempts.resendAction',
            defaultMessage: 'Resend',
            description: 'Action to resend attempt',
          }),
          onClick: (attempt: Attempt) => {
            handleUpdateAttempt(attempt, 'resend');
          },
        });
      }

      if (enableWebhookExtraFeatures) {
        actions.push({
          key: ATTEMPT_MODEL_ACTION_KEYS.ARCHIVE,
          label: intl.formatMessage({
            id: 'Attempts.archiveAction',
            defaultMessage: 'Archive',
            description: 'Action to archive attempt',
          }),
          onClick: (attempt: Attempt) => {
            handleUpdateAttempt(attempt, 'archive');
          },
        });
      }

      return actions;
    },
    [enableWebhookExtraFeatures, isLoadingAttempts, data, intl],
  );

  return (
    <Layout preset="sections">
      <AttemptsFilters
        filters={filters}
        onChange={(updatedFilters: Filter[]) => {
          dispatch(slices.webhooks.actions.setFilters(updatedFilters));
        }}
      />
      {data.total > DEFAULT_LIMIT_ATTEMPTS && (
        <StatusMessage
          message={intl.formatMessage({
            id: 'Attempts.maxResultsMessage',
            defaultMessage:
              'The current results are over the maximum limit of 50,000. Change the filters to get 50,000 or fewer results.',
            description: 'Warning message about maximum results limit',
          })}
          status="warning"
        />
      )}
      <ContentContainer placeholderContent={placeholderContent}>
        <PersistedTable
          actions={
            enableWebhookExtraFeatures
              ? [
                  {
                    label: intl.formatMessage({
                      id: 'Attempts.exportAction',
                      defaultMessage: 'Export',
                      description: 'Export button label',
                    }),
                    icon: 'download',
                    disabled: isLoadingExportAttempts,
                    tooltip: disabledTooltip,
                    onClick: (tableState: any) => {
                      const { columnOrder, name } = tableState;

                      getAttemptsExport({
                        filters,
                        sortBy,
                      }).then(async (response) => {
                        const prepareResponse = (
                          response.data?.results ?? []
                        ).map((record) => {
                          return columnOrder.reduce(
                            (newRecord: any, key: string) => {
                              if (key in record) {
                                newRecord[key] = record[key as keyof Attempt];
                              }

                              return newRecord;
                            },
                            {},
                          );
                        });

                        try {
                          await exportXlsx({
                            data: prepareResponse,
                            name,
                          });
                          toast({
                            status: 'success',
                            message: intl.formatMessage({
                              id: 'Attempts.exportSuccessMessage',
                              defaultMessage: 'Attempts exported successfully.',
                              description: 'Success message for export',
                            }),
                          });
                        } catch {
                          toast({
                            message: intl.formatMessage({
                              id: 'Attempts.exportErrorMessage',
                              defaultMessage:
                                'An error occurred while exporting the data',
                              description: 'Error message for export failure',
                            }),
                            status: 'danger',
                          });
                        }
                      });
                    },
                  },
                ]
              : []
          }
          columns={columns}
          context={TableContextType.Attempts}
          data={data.results}
          isLoading={isLoadingAttempts}
          options={{
            enableManageColumns: true,
            enableSelectRows: false,
            enableExportXlsx: false,
            enablePagination: true,
            enablePageSizeSelect: true,
            pageSizes: [50, 100, 250, 500],
          }}
          getRowActions={getRowActions}
          rowDetails={{
            onClick: (attempt: Attempt) => {
              handleUpdateAttempt(attempt, 'view');
            },
          }}
          onPaginate={handlePaginate}
          onUpdate={handleUpdate}
          reactTableOptions={{
            manualSortBy: true,
          }}
          state={{
            pageIndex,
            pageSize,
            sortBy: [sortBy],
          }}
          totalCount={
            data.total <= DEFAULT_LIMIT_ATTEMPTS
              ? data.total
              : DEFAULT_LIMIT_ATTEMPTS
          }
        />
      </ContentContainer>
    </Layout>
  );
};
